import { FC, useEffect, useState, useMemo } from "react";
import { Box, Stack, useTheme } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ReactTable,
  useDispatchOnParams,
  ActivateKeyForm,
  AddNewKeysForm,
  types,
  DeleteConfirmationModal,
  BlockKeyConfirmationModal,
  Topbar,
  toast,
  utils,
  shared,
  KeyManagerConfirmationModal
} from "@vilocnv/allsetra-core";
import { FormikHelpers } from "formik";
import { compact } from "lodash";
import AddIcon from "@mui/icons-material/Add";

// Data
import { useAppDispatch, useAppSelector, useCurrentUser } from "hooks";
import {
  getAccountKeysByQueryThunk,
  activateKeyThunk,
  deactivateKeyThunk,
  getAvailableKeysByAccountThunk,
  getEligibleUsersForKeyAssignmentThunk
} from "app/features";
import {
  selectAccountKeysState,
  selectDrawerSelectedAccountState,
  selectAccountAvailableKeysState,
  selectUsersState
} from "app/data/selectors";
import {
  ACCOUNT_KEYS_CSV_COLUMNS,
  ACCOUNT_KEYS_TABLE_COLUMNS
} from "app/data/constants";
import { ExportCsvButton } from "components/common/ExportCsvButton";
import { useTranslation } from "react-i18next";
import { SignalRService } from "app/data/services";
import postKeysAction from "domain/keys/store/actions/post-keys.action";
import deleteKeyAction from "domain/keys/store/actions/delete-key.action";
import putKeyInventoryAction from "domain/keys/store/actions/put-key-inventory.action";
import blockKeyAction from "domain/keys/store/actions/block-key.action";
import unblockKeyAction from "domain/keys/store/actions/unblock-key.action";
import getKeyHistoryAction from "domain/keys/store/actions/get-key-history.action";
import {
  KeyHistoryEntryDto,
  KeyStatus
} from "domain/keys/entities/dtos/key-history.dto";
import { KeyHistoryModal } from "domain/keys/components/history";
import { KeyStatusFilter } from "domain/keys/components/KeyStatusFilter";

const KeysManager: FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { isDriver } = useCurrentUser();
  const navigate = useNavigate();

  // Global State
  const { drawerSelectedAccountId: accountId } = useAppSelector(
    selectDrawerSelectedAccountState
  );
  const { accountKeys, totalRecords, loading } = useAppSelector(
    selectAccountKeysState
  );
  const { accountAvailableKeys } = useAppSelector(
    selectAccountAvailableKeysState
  );
  const { allUsers } = useAppSelector(selectUsersState);

  const [keyStatusFilter, setKeyStatusFilter] = useState<KeyStatus | "all">(
    KeyStatus.Active
  );

  const { t, i18n } = useTranslation([
    "translation",
    "tableHeadingsTranslation",
    "formFieldsTranslation"
  ]);

  const tableColumns = useMemo(
    () => ACCOUNT_KEYS_TABLE_COLUMNS(t, accountKeys, keyStatusFilter),
    [t, i18n.language, accountKeys, keyStatusFilter]
  );

  const csvColumns = useMemo(
    () => ACCOUNT_KEYS_CSV_COLUMNS(t),
    [t, i18n.language]
  );

  useEffect(() => {
    if (accountId) {
      dispatch(getAvailableKeysByAccountThunk(accountId || ""));
      dispatch(getEligibleUsersForKeyAssignmentThunk(accountId || ""));
    }
  }, [accountId]);

  // Local State
  const [searchByValue, setSearchByValue] = useState<string>("keyId");
  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null);
  const [activateKeyModal, setActivateKeyModal] = useState<boolean>(false);
  const [addNewKeysModal, setAddNewKeysModal] = useState<boolean>(false);
  const [openDeactivateModal, setOpenDeactivateModal] =
    useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openInventoryModal, setOpenInventoryModal] = useState<boolean>(false);
  const [openBlockConfirmationModal, setOpenBlockConfirmationModal] =
    useState<boolean>(false);
  const [blockKeyData, setBlockKeyData] = useState<types.IKey | null>(null);
  const [openUnblockModal, setOpenUnblockModal] = useState<boolean>(false);
  const [openHistoryModal, setOpenHistoryModal] = useState<boolean>(false);
  const [historyData, setHistoryData] = useState<KeyHistoryEntryDto[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [selectedKeyLabel, setSelectedKeyLabel] = useState<string>("");
  const [isSubmittingRequest, setIsSubmittingRequest] =
    useState<boolean>(false);
  const [showAssignKeyModal, setShowAssignKeyModal] = useState<boolean>(false);
  const [selectedKey, setSelectedKey] = useState<types.IKey | null>(null);

  const [searchParams] = useSearchParams();

  const dispatchArgs = useMemo(() => {
    const searchValue = searchParams.get("search") || "";
    const whereConditions: any[] =
      keyStatusFilter === "all"
        ? []
        : [{ field: "KeyStatus", value: keyStatusFilter }];

    if (searchValue && searchByValue) {
      const fieldName =
        searchByValue.charAt(0).toUpperCase() + searchByValue.slice(1);
      whereConditions.push({ field: fieldName, value: searchValue });
    }

    return {
      accountId: accountId || "",
      params: {
        ...utils.getCommonParamsForApi(),
        where: whereConditions,
        searchByField: searchByValue
      }
    };
  }, [accountId, keyStatusFilter, searchByValue, searchParams]);

  useDispatchOnParams(getAccountKeysByQueryThunk, {
    args: dispatchArgs
  });

  const toggleActivateKeyModal = () => setActivateKeyModal(!activateKeyModal);
  const toggleAddNewKeysModal = () => setAddNewKeysModal(!addNewKeysModal);

  const refreshKeysTable = async () => {
    await dispatch(getAccountKeysByQueryThunk(dispatchArgs));
  };

  const onActivateKeyHandler = async (
    values: types.IActivateKey,
    formikHelpers: FormikHelpers<types.IActivateKey>
  ) => {
    formikHelpers.setSubmitting(true);
    setIsSubmittingRequest(true);

    const payload: any = {
      accountId: accountId,
      keyId: values.keyId
    };

    if (values.assignedUser) {
      payload.assignedUser = values.assignedUser;
    } else if (values.label) {
      payload.label = values.label;
    }

    const { type, payload: responsePayload } = await dispatch(
      activateKeyThunk(payload)
    );

    if (type === "accounts/activateKeyThunk/fulfilled") {
      try {
        await shared.waitSinalREvent({
          hubConnection: SignalRService.hubConnection,
          filterFn: (event) =>
            event?.eventName ===
            types.BackendEventsEnum.AccountKeyActivatedEvent
        });
      } catch {
        // Continue even if SignalR event times out
      } finally {
        formikHelpers.setSubmitting(false);
        setIsSubmittingRequest(false);
        formikHelpers.resetForm();
        toggleActivateKeyModal();
        toast.success(t("toasts.activateKeySuccess"));
        await refreshKeysTable();
      }
    } else {
      formikHelpers.setSubmitting(false);
      setIsSubmittingRequest(false);

      const errorMessage =
        typeof responsePayload === "string"
          ? responsePayload
          : (responsePayload as any)?.message ||
            (responsePayload as any)?.error ||
            "";

      if (
        errorMessage.toLowerCase().includes("user already has key assigned")
      ) {
        toast.error(t("toasts.userAlreadyHasKey"));
      } else if (errorMessage) {
        toast.error(errorMessage);
      } else {
        toast.error(t("toasts.activateKeyError"));
      }
    }
  };

  const onAssignKeyToDriverHandler = async (
    values: types.IActivateKey,
    formikHelpers: FormikHelpers<types.IActivateKey>
  ) => {
    formikHelpers.setSubmitting(true);
    setIsSubmittingRequest(true);

    const payload: any = {
      accountId: accountId,
      keyId: values.keyId
    };

    if (values.assignedUser) {
      payload.assignedUser = values.assignedUser;
    } else if (values.label) {
      payload.label = values.label;
    }

    const { type, payload: responsePayload } = await dispatch(
      activateKeyThunk(payload)
    );

    if (type === "accounts/activateKeyThunk/fulfilled") {
      try {
        await shared.waitSinalREvent({
          hubConnection: SignalRService.hubConnection,
          filterFn: (event) =>
            event?.eventName ===
            types.BackendEventsEnum.AccountKeyActivatedEvent
        });
      } catch {
        // Continue even if SignalR event times out
      } finally {
        formikHelpers.setSubmitting(false);
        setIsSubmittingRequest(false);
        formikHelpers.resetForm();
        setShowAssignKeyModal(false);
        toast.success(t("toasts.activateKeySuccess"));
        await refreshKeysTable();
      }
    } else {
      formikHelpers.setSubmitting(false);
      setIsSubmittingRequest(false);

      const errorMessage =
        typeof responsePayload === "string"
          ? responsePayload
          : (responsePayload as any)?.message ||
            (responsePayload as any)?.error ||
            "";

      if (
        errorMessage.toLowerCase().includes("user already has key assigned")
      ) {
        toast.error(t("toasts.userAlreadyHasKey"));
      } else if (errorMessage) {
        toast.error(errorMessage);
      } else {
        toast.error(t("toasts.activateKeyError"));
      }
    }
  };

  const onDeactivateKeyHandler = async () => {
    if (!selectedKeyId || !accountId) return;

    setIsSubmittingRequest(true);

    const { type } = await dispatch(
      deactivateKeyThunk({
        accountId: accountId,
        keyId: selectedKeyId
      })
    );

    if (type === "accounts/deactivateKeyThunk/fulfilled") {
      try {
        await shared.waitSinalREvent({
          hubConnection: SignalRService.hubConnection,
          filterFn: (event) =>
            event?.eventName ===
            types.BackendEventsEnum.AccountKeyDeactivatedEvent
        });

        setIsSubmittingRequest(false);
        setOpenDeactivateModal(false);
        toast.success(t("toasts.deactivateKeySuccess"));
        await refreshKeysTable();
      } catch {
        setIsSubmittingRequest(false);
        setOpenDeactivateModal(false);
        toast.success(t("toasts.deactivateKeySuccess"));
        await refreshKeysTable();
      }
    } else {
      setIsSubmittingRequest(false);
    }
  };

  const onDeleteKeyHandler = async () => {
    if (!selectedKeyId || !accountId) return;

    setIsSubmittingRequest(true);

    const { type } = await dispatch(
      deleteKeyAction({
        accountId: accountId,
        keyId: selectedKeyId
      })
    );

    if (type === "keys/deleteKeyAction/fulfilled") {
      setIsSubmittingRequest(false);
      setOpenDeleteModal(false);
      toast.success(t("toasts.deleteKeySuccess"));
      await refreshKeysTable();
    } else {
      setIsSubmittingRequest(false);
    }
  };

  const onPutKeyInventoryHandler = async () => {
    if (!selectedKeyId || !accountId) return;

    setIsSubmittingRequest(true);

    const { type } = await dispatch(
      putKeyInventoryAction({
        accountId: accountId,
        keyId: selectedKeyId
      })
    );

    if (type === "keys/putKeyInventoryAction/fulfilled") {
      setIsSubmittingRequest(false);
      setOpenInventoryModal(false);
      toast.success(t("toasts.putKeyInventorySuccess"));
      await refreshKeysTable();
    } else {
      setIsSubmittingRequest(false);
    }
  };

  const onBlockKeyHandler = async (shouldUnassignUser: boolean) => {
    if (!blockKeyData || !accountId) return;

    setIsSubmittingRequest(true);

    const { type } = await dispatch(
      blockKeyAction({
        accountId: accountId,
        keyId: blockKeyData.uniqueId,
        shouldUnassignUser
      })
    );

    if (type === "keys/blockKeyAction/fulfilled") {
      setIsSubmittingRequest(false);
      setOpenBlockConfirmationModal(false);
      setBlockKeyData(null);
      toast.success(t("toasts.blockKeySuccess"));
      await refreshKeysTable();
    } else {
      setIsSubmittingRequest(false);
    }
  };

  const onUnblockKeyHandler = async () => {
    if (!selectedKeyId || !accountId) return;

    setIsSubmittingRequest(true);

    const { type, payload } = await dispatch(
      unblockKeyAction({
        accountId: accountId,
        keyId: selectedKeyId
      })
    );

    if (type === "keys/unblockKeyAction/fulfilled") {
      const resultingStatus = (payload as any)?.resultingStatus;

      if (resultingStatus === KeyStatus.Active) {
        toast.success(t("toasts.unblockKeySuccessActive"));
      } else if (resultingStatus === KeyStatus.Inventory) {
        toast.success(t("toasts.unblockKeySuccessInventory"));
      } else {
        toast.success(t("toasts.unblockKeySuccess"));
      }

      await new Promise((resolve) => setTimeout(resolve, 1500));

      await Promise.all([
        refreshKeysTable(),
        dispatch(getAvailableKeysByAccountThunk(accountId || ""))
      ]);

      setIsSubmittingRequest(false);
      setOpenUnblockModal(false);
    } else {
      setIsSubmittingRequest(false);
    }
  };

  const onViewHistoryHandler = async (key: types.IKey) => {
    setSelectedKeyId(key.uniqueId);
    setSelectedKeyLabel(key.label || "");
    setOpenHistoryModal(true);
    setHistoryLoading(true);
    setHistoryData([]);

    const { type, payload } = await dispatch(
      getKeyHistoryAction({
        keyId: key.uniqueId,
        accountId: accountId || ""
      })
    );

    if (type === "keys/getKeyHistoryAction/fulfilled") {
      setHistoryData(payload as KeyHistoryEntryDto[]);
    }
    setHistoryLoading(false);
  };

  const onAddNewKeysHandler = async (
    values: types.ICreateKeys,
    formikHelpers: FormikHelpers<types.ICreateKeys>
  ) => {
    formikHelpers.setSubmitting(true);
    setIsSubmittingRequest(true);

    const keyIds = compact(
      values.keyIds
        .split(/\r?\n/)
        .map((keyId) => keyId.trim())
        .filter((keyId) => keyId.length > 0)
    );

    const { type, payload } = await dispatch(
      postKeysAction({
        accountId: accountId || "",
        keyIds
      })
    );

    if (type === "keys/postKeysAction/fulfilled") {
      try {
        await shared.waitSinalREvent({
          hubConnection: SignalRService.hubConnection,
          filterFn: (event) =>
            event?.eventName === types.BackendEventsEnum.AccountKeyCreatedEvent
        });

        await new Promise((resolve) => setTimeout(resolve, 800));

        formikHelpers.setSubmitting(false);
        setIsSubmittingRequest(false);
        formikHelpers.resetForm();
        toggleAddNewKeysModal();
        toast.success(t("toasts.createKeysSuccess"));

        await dispatch(getAvailableKeysByAccountThunk(accountId || ""));

        if (keyStatusFilter === KeyStatus.Inventory) {
          await refreshKeysTable();
        } else {
          setKeyStatusFilter(KeyStatus.Inventory);
        }
      } catch {
        await new Promise((resolve) => setTimeout(resolve, 800));

        formikHelpers.setSubmitting(false);
        setIsSubmittingRequest(false);
        formikHelpers.resetForm();
        toggleAddNewKeysModal();
        toast.success(t("toasts.createKeysSuccess"));

        await dispatch(getAvailableKeysByAccountThunk(accountId || ""));

        if (keyStatusFilter === KeyStatus.Inventory) {
          await refreshKeysTable();
        } else {
          setKeyStatusFilter(KeyStatus.Inventory);
        }
      }
    } else {
      formikHelpers.setSubmitting(false);
      setIsSubmittingRequest(false);

      if ((payload as any)?.response?.status === 409) {
        toast.error(t("toasts.createKeysDuplicateError"));
      }
    }
  };

  const onAssignKeyForDriverHandler = async (key: types.IKey) => {
    setSelectedKey(key);
    setShowAssignKeyModal(true);
  };

  useEffect(() => {
    if (isDriver) {
      navigate("/dashboard", { replace: true });
    }
  }, [isDriver, navigate]);

  return (
    <main>
      <Topbar
        theme={theme}
        title={t("drawerMenuLinks.keysManager")}
        searchField={{
          placeholder: t("common.search"),
          sx: {
            "& .MuiSvgIcon-root": {
              fill: "#959EB2"
            }
          }
        }}
        searchByOptions={[
          {
            value: "keyId",
            label: t("tableHeading.keyID", {
              ns: "tableHeadingsTranslation"
            })
          },
          {
            value: "label",
            label: t("tableHeading.label", {
              ns: "tableHeadingsTranslation"
            })
          }
        ]}
        searchByValue={searchByValue}
        setSearchByValue={setSearchByValue}
        translator={t}
        searchByWidth="160px"
      />
      <Box mx={4}>
        <ReactTable
          columns={tableColumns}
          data={accountKeys ?? []}
          isSelectable={false}
          isLoading={loading as any}
          paginationTotalRows={totalRecords as any}
          sx={undefined}
          moduleName="key"
          primaryButton={{
            id: "keys-add-new",
            text: t("buttonsText.addNewKeys"),
            variant: "outlined",
            startIcon: <AddIcon />,
            onClick: toggleAddNewKeysModal
          }}
          secondaryButton={{
            id: "keys-activate-form",
            text: t("buttonsText.activateNewKeys"),
            variant: "outlined",
            onClick: toggleActivateKeyModal
          }}
          tableMainBoxStyles={{
            height: "calc(100vh - 314px)",
            border: "1px solid #D5D9E8",
            borderRadius: "10px",
            marginBottom: "10vh",
            padding: "1px",
            "& tbody": {
              "& tr": {
                "& td": {
                  padding: "10px 16px"
                }
              }
            }
          }}
          mainTableStyles={{
            height: accountKeys?.length > 0 ? "fit-content" : "100%"
          }}
          cellActions={[
            {
              name: t("buttonsText.viewHistory"),
              onClick: (key: types.IKey) => onViewHistoryHandler(key)
            },
            {
              name: t("buttonsText.deleteKey"),
              when: (key: types.IKey & { keyStatus?: KeyStatus }) =>
                key?.keyStatus !== KeyStatus.Deleted,
              onClick: (key: types.IKey) => {
                setIsSubmittingRequest(false);
                setSelectedKeyId(key.uniqueId);
                setSelectedKey(key);
                setOpenDeleteModal(true);
              }
            },
            {
              name: t("buttonsText.putInInventory"),
              when: (key: types.IKey & { keyStatus?: KeyStatus }) =>
                key?.keyStatus !== KeyStatus.Inventory &&
                key?.keyStatus !== KeyStatus.Blocked &&
                key?.keyStatus !== KeyStatus.Deleted,
              onClick: (key: types.IKey) => {
                setIsSubmittingRequest(false);
                setSelectedKeyId(key.uniqueId);
                setSelectedKey(key);
                setOpenInventoryModal(true);
              }
            },
            {
              name: t("buttonsText.blockKey"),
              when: (key: types.IKey & { keyStatus?: KeyStatus }) =>
                key?.keyStatus !== KeyStatus.Blocked &&
                key?.keyStatus !== KeyStatus.Inventory &&
                key?.keyStatus !== KeyStatus.Deleted,
              onClick: (key: types.IKey) => {
                setBlockKeyData(key);
                setOpenBlockConfirmationModal(true);
              }
            },
            {
              name: t("buttonsText.unblockKey"),
              when: (key: types.IKey & { keyStatus?: KeyStatus }) =>
                key?.keyStatus === KeyStatus.Blocked,
              onClick: (key: types.IKey) => {
                setIsSubmittingRequest(false);
                setSelectedKeyId(key.uniqueId);
                setSelectedKey(key);
                setOpenUnblockModal(true);
              }
            },
            {
              name: t("buttonsText.assignKeyDriver"),
              when: (key: types.IKey & { keyStatus?: KeyStatus }) =>
                key?.keyStatus === KeyStatus.Inventory,
              onClick: (key: types.IKey) => {
                setIsSubmittingRequest(false);
                onAssignKeyForDriverHandler(key);
              }
            }
          ]}
          translator={t}
          headerChildren={
            <Stack direction="row" spacing={2} alignItems="center">
              <KeyStatusFilter
                key={`key-status-filter-${keyStatusFilter}`}
                value={keyStatusFilter}
                onChange={setKeyStatusFilter}
              />
              <ExportCsvButton
                id="keys-export-csv"
                data={accountKeys ?? []}
                columns={csvColumns}
                filenamePrefix="keys"
                disabled={Boolean(loading)}
              />
            </Stack>
          }
        />
        <ActivateKeyForm
          open={activateKeyModal}
          onClose={toggleActivateKeyModal}
          onSubmit={onActivateKeyHandler}
          keys={accountAvailableKeys ?? []}
          users={allUsers ?? []}
          submitting={isSubmittingRequest}
          theme={theme}
          translator={t}
        />
        <AddNewKeysForm
          open={addNewKeysModal}
          onClose={toggleAddNewKeysModal}
          onSubmit={onAddNewKeysHandler}
          theme={theme}
          submitting={isSubmittingRequest}
          translator={t}
        />
        {/* Deactivate Key Modal */}
        <DeleteConfirmationModal
          open={openDeactivateModal}
          onClose={() => setOpenDeactivateModal(false)}
          title={t("typography.deleteKeyTitle")}
          subTitle={t("typography.deleteKeySubtitle")}
          primaryBtnProps={{
            id: "keys-deactivate",
            text: t("buttonsText.deactivate"),
            onClick: onDeactivateKeyHandler,
            loading: isSubmittingRequest
          }}
          secondaryBtnProps={{
            text: t("buttonsText.cancel")
          }}
          theme={theme}
        />
        {/* Delete Key Modal */}
        <KeyManagerConfirmationModal
          isOpen={openDeleteModal}
          onClose={() => setOpenDeleteModal(false)}
          onConfirm={(shouldUnassign) => onDeleteKeyHandler()}
          keyData={selectedKey}
          theme={theme}
          translator={t}
          isLoading={isSubmittingRequest}
          title={t("typography.deleteKeyPermanentTitle")}
          description={t("typography.deleteKeyPermanentSubtitle")}
          confirmButtonText={t("buttonsText.confirm")}
          cancelButtonText={t("buttonsText.cancel")}
        />
        {/* Put In Inventory Modal */}
        <KeyManagerConfirmationModal
          isOpen={openInventoryModal}
          onClose={() => setOpenInventoryModal(false)}
          onConfirm={(shouldUnassign) => onPutKeyInventoryHandler()}
          keyData={selectedKey}
          theme={theme}
          translator={t}
          isLoading={isSubmittingRequest}
          title={t("typography.putKeyInventoryTitle")}
          description={t("typography.putKeyInventorySubtitle")}
          confirmButtonText={t("buttonsText.putInInventory")}
          cancelButtonText={t("buttonsText.cancel")}
        />
        {/* Block Key Confirmation Modal */}
        <KeyManagerConfirmationModal
          isOpen={openBlockConfirmationModal}
          onClose={() => setOpenBlockConfirmationModal(false)}
          onConfirm={onBlockKeyHandler}
          keyData={blockKeyData}
          theme={theme}
          translator={t}
          isLoading={isSubmittingRequest}
          title={t("typography.blockKeyTitle")}
          description={t("typography.blockKeyConfirmationDescription")}
          confirmButtonText={t("buttonsText.blockKey")}
          cancelButtonText={t("buttonsText.cancel")}
          showRadioOptions={true}
          radioQuestion={t("typography.shouldUnassignUserQuestion")}
          radioOptions={{
            yes: {
              value: "yes",
              label: t("typography.yes"),
              description: t("typography.keyReturnToInventoryOnUnblock")
            },
            no: {
              value: "no",
              label: t("typography.no"),
              description: t("typography.keyRemainAssignedOnUnblock")
            }
          }}
          notActivatedMessage={t("typography.blockKeyLabelActivationMessage")}
        />
        {/* Unblock Key Modal */}
        <KeyManagerConfirmationModal
          isOpen={openUnblockModal}
          onClose={() => setOpenUnblockModal(false)}
          onConfirm={(shouldUnassign) => onUnblockKeyHandler()}
          keyData={selectedKey}
          theme={theme}
          translator={t}
          isLoading={isSubmittingRequest}
          title={t("typography.unblockKeyTitle")}
          description={t("typography.unblockKeySubtitle")}
          confirmButtonText={t("buttonsText.unblockKey")}
          cancelButtonText={t("buttonsText.cancel")}
        />
        {/* Key History Modal */}
        <KeyHistoryModal
          open={openHistoryModal}
          onClose={() => setOpenHistoryModal(false)}
          theme={theme}
          historyData={historyData}
          loading={historyLoading}
          keyLabel={selectedKeyLabel}
        />
        {/* Assign Key to Driver Modal */}
        <ActivateKeyForm
          open={showAssignKeyModal}
          onClose={() => setShowAssignKeyModal(false)}
          onSubmit={onAssignKeyToDriverHandler}
          keys={selectedKey ? [selectedKey] : []}
          users={allUsers ?? []}
          submitting={isSubmittingRequest}
          theme={theme}
          translator={t}
        />
      </Box>
    </main>
  );
};

export default KeysManager;
